import React, { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { useLang } from "../context/LanguageContext";

const PHOTOS = ["/couple-1.png", "/couple-2.png"];
// Total cycle = visible time (3s) + fade duration (2.2s)
const PHOTO_INTERVAL = 5200;
const FADE_DURATION = 2.2;

export default function OurStory() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const photoY = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const textY = useTransform(scrollYProgress, [0, 1], [-40, 40]);

  const [photoIdx, setPhotoIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setPhotoIdx((i) => (i + 1) % PHOTOS.length);
    }, PHOTO_INTERVAL);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      id="story"
      ref={ref}
      className="relative py-24 md:py-40 px-6 md:px-12 bg-cream/50"
      data-testid="story-section"
    >
      <div
        className="brush-blob bg-burgundy/30"
        style={{ width: 420, height: 420, top: "-120px", left: "-80px" }}
      />

      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-center">
        {/* Text */}
        <motion.div style={{ y: textY }} className="md:pr-8 md:pl-12">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9 }}
            className="text-xs uppercase tracking-[0.4em] text-burgundy mb-4"
          >
            {t.story.overline}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.1, delay: 0.1 }}
            className="font-serif font-light text-4xl md:text-6xl text-ink leading-tight mb-8"
          >
            {t.story.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.1, delay: 0.25 }}
            className="font-serif italic text-lg md:text-xl text-ink/75 leading-relaxed"
            data-testid="story-body"
          >
            {t.story.body}
          </motion.p>

          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.4, delay: 0.4 }}
            className="mt-10 h-[1px] bg-burgundy origin-left w-32"
          />
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-3 font-script text-4xl text-burgundy"
          >
            N & R
          </motion.p>
        </motion.div>

        {/* Photos slideshow */}
        <motion.div style={{ y: photoY }} className="relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="relative aspect-[4/5] w-full overflow-hidden rounded-t-[50%] rounded-b-md bg-cream"
            data-testid="story-photos"
          >
            <AnimatePresence mode="sync">
              <motion.img
                key={photoIdx}
                src={PHOTOS[photoIdx]}
                alt="Natanael & Raluka"
                initial={{ opacity: 0, scale: 1.05 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1 }}
                transition={{ duration: FADE_DURATION, ease: [0.4, 0, 0.2, 1] }}
                className="absolute inset-0 w-full h-full object-cover"
                draggable={false}
              />
            </AnimatePresence>
            <div className="absolute inset-0 bg-gradient-to-t from-ink/20 via-transparent to-transparent pointer-events-none" />
          </motion.div>
          <div className="absolute -inset-3 border border-wedding-border -z-10 translate-x-4 translate-y-4 hidden md:block" />
        </motion.div>
      </div>
    </section>
  );
}
